import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import {
  BackendResponse,
  BatchCopyWorkflowsResponse,
  EmailDynamicContent,
  EmailTemplate,
  WorkflowPayload,
} from '@qtek/shared/models';
import { filter, Observable } from 'rxjs';
import { TriggerService } from './trigger.service';

export const MY_DEALS_WORKFLOWS_REF_ID = 'myDeals';

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  private readonly webSocketService = inject(WebSocketService);
  private readonly triggerService = inject(TriggerService);
  private readonly http = inject(HttpClient);

  entityName: WebsocketEntTypes = 'workflow';
  wsManager = new WSEntityManager(this.webSocketService, this.entityName);

  readonly webSocketIsAlive$ = this.webSocketService.getWebSocketIsAlive$();

  subscribe(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>,
    refId: string
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity(payload);
  }

  subscribeMyDealsWorkflows(): Observable<BaseWebsocketMessage> {
    // Fire-and-forget the query per the BE contract for the cross-deal
    // workflow list. The response broadcasts to all `ent === 'workflow'`
    // subscribers; the SD-1977 effect filters and dispatches the success
    // action. Per-deal subscriptions discard cross-talk via a refId check.
    this.wsManager.query({ id: MY_DEALS_WORKFLOWS_REF_ID });
    return this.webSocketService
      .getWebSocket$()
      .pipe(filter((msg: any) => msg?.ent === this.entityName));
  }

  batchCopyWorkflows(
    sourceWorkflowIds: string[],
    targetDealRefId: string
  ): Observable<BackendResponse<BatchCopyWorkflowsResponse>> {
    return this.http.post<BackendResponse<BatchCopyWorkflowsResponse>>(
      '/api/v1/workflow/batch-copy',
      { sourceWorkflowIds, targetDealRefId },
      { withCredentials: true }
    );
  }

  createWorkflow(payload: WorkflowPayload): void {
    this.wsManager.create({ res: payload });
  }

  updateWorkflow(payload: WorkflowPayload, id: string): void {
    this.wsManager.update({ res: payload, id });
  }

  deleteWorkflow(id: string, refId?: string): void {
    this.wsManager.delete({ id, mysid: refId });
  }

  getWorkflow(id: string): void {
    this.wsManager.query({ id });
  }

  getWorkflowDetail(id: string): void {
    this.wsManager.get({ view: 'dtl', id });
  }

  subscribeWorkflowDetail(
    workflowId: string
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity({
      view: 'dtl',
      mysid: workflowId,
      q: [{ k: '_id', op: 'EQ' as const, v: [workflowId] }],
    });
  }

  // Trigger methods delegated to TriggerService
  get triggerManager() {
    return this.triggerService.triggerManager;
  }

  triggerWorkflow(
    triggerId: string,
    refId: string
  ): Observable<BackendResponse> {
    return this.http.post<BackendResponse>(
      `/api/v1/service/startTrigger/${triggerId}`,
      { refId },
      { withCredentials: true }
    );
  }

  getParticipantsByRoles(
    itemId: string,
    selectedRoles: string[]
  ): Observable<
    BackendResponse<{
      internalParticipants: Record<string, number>;
      externalParticipants: Record<string, number>;
      allParticipants: Record<string, number>;
    }>
  > {
    const params = { itemId, selectedRoles: selectedRoles.join(',') };
    return this.http.get<
      BackendResponse<{
        internalParticipants: Record<string, number>;
        externalParticipants: Record<string, number>;
        allParticipants: Record<string, number>;
      }>
    >('/api/v1/service/user-with-role-in-deal', {
      params,
      withCredentials: true,
    });
  }

  getEmailDynamicContent(
    taskId?: string
  ): Observable<BackendResponse<EmailDynamicContent>> {
    const params = taskId ? { taskId } : {};
    return this.http.get<BackendResponse<EmailDynamicContent>>(
      '/api/v1/service/email-dynamic-content',
      {
        params,
        withCredentials: true,
      }
    );
  }

  getEmailTemplates(): Observable<BackendResponse<EmailTemplate[]>> {
    return this.http.get<BackendResponse<EmailTemplate[]>>(
      '/api/v1/service/email-templates',
      {
        withCredentials: true,
      }
    );
  }
}
